import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Alert, Spinner, Modal, Pagination } from 'react-bootstrap';
import { apiService } from '../../services/api';

interface Payment {
  id: string;
  plateNumber: string;
  spaceNumber: string;
  entryTime: string;
  exitTime?: string;
  duration: string;
  amount: number;
  status: 'pending' | 'paid' | 'overdue' | 'refunded';
  paymentMethod?: 'cash' | 'card' | 'mobile' | 'online';
  paymentTime?: string;
  transactionId?: string;
  ownerName: string;
  ownerPhone: string;
}

const PaymentManagement: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchPlate, setSearchPlate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const paymentsPerPage = 10;

  // 載入支付數據
  const loadPayments = async () => {
    try {
      setLoading(true);
      setError('');
      
      // 從API獲取支付數據
      const response = await apiService.getPayments({
        page: currentPage,
        limit: paymentsPerPage,
        search: searchPlate,
        status: statusFilter === 'all' ? undefined : statusFilter,
        paymentMethod: paymentMethodFilter === 'all' ? undefined : paymentMethodFilter
      });
      
      if (response.success && response.data) {
        // 處理API返回的數據結構
        let paymentsData: any[] = [];
        
        if (response.data.items && Array.isArray(response.data.items)) {
          // 如果是分頁數據結構
          paymentsData = response.data.items;
        } else if (Array.isArray(response.data)) {
          // 如果直接是數組
          paymentsData = response.data;
        } else {
          // 如果沒有數據，使用空數組
          paymentsData = [];
        }
        
        // 將API數據格式轉換為前端格式
        const formattedPayments: Payment[] = paymentsData.map((payment: any) => {
          // 處理停車會話數據
          const session = payment.parkingSession || payment.session;
          const vehicle = session?.vehicle || payment.vehicle;
          const spot = session?.parkingSpot || session?.spot || payment.spot;
          
          // 計算停車時長
          let duration = 'N/A';
          if (session?.entryTime) {
            const entryTime = new Date(session.entryTime);
            const exitTime = session.exitTime ? new Date(session.exitTime) : new Date();
            const diffMs = exitTime.getTime() - entryTime.getTime();
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            duration = `${diffHours}小時${diffMinutes}分鐘`;
          }
          
          return {
            id: payment._id || payment.id || `payment_${Date.now()}_${Math.random()}`,
            plateNumber: vehicle?.licensePlate || payment.plateNumber || 'N/A',
            spaceNumber: spot?.spotNumber || payment.spaceNumber || 'N/A',
            entryTime: session?.entryTime ? new Date(session.entryTime).toLocaleString('zh-TW') : payment.entryTime || 'N/A',
            exitTime: session?.exitTime ? new Date(session.exitTime).toLocaleString('zh-TW') : payment.exitTime,
            duration: duration,
            amount: payment.amount || payment.totalAmount || 0,
            status: payment.status || 'pending',
            paymentMethod: payment.method || payment.paymentMethod,
            paymentTime: payment.paidAt ? new Date(payment.paidAt).toLocaleString('zh-TW') : payment.paymentTime,
            transactionId: payment.transactionId || payment.paymentId || payment.id,
            ownerName: vehicle?.ownerName || payment.ownerName || 'N/A',
            ownerPhone: vehicle?.ownerPhone || payment.ownerPhone || 'N/A'
          };
        });
        
        setPayments(formattedPayments);
      } else {
        // 如果沒有數據或API返回失敗，設置空數組
        setPayments([]);
        if (!response.success) {
          throw new Error(response.message || '獲取支付數據失敗');
        }
      }
    } catch (error: any) {
      console.error('載入支付數據失敗:', error);
      
      // 提供更詳細的錯誤信息
      let errorMessage = '載入支付數據失敗，請稍後再試';
      
      if (error.response?.status === 401) {
        errorMessage = '認證失敗，請重新登入';
      } else if (error.response?.status === 403) {
        errorMessage = '權限不足，無法訪問支付數據';
      } else if (error.response?.status === 429) {
        errorMessage = '請求過於頻繁，請稍後再試';
      } else if (error.response?.data?.message) {
        errorMessage = `載入支付數據失敗: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage = `載入支付數據失敗: ${error.message}`;
      }
      
      setError(errorMessage);
      setPayments([]); // 設置空數組以避免渲染錯誤
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'danger';
      case 'refunded': return 'info';
      default: return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid': return '已付款';
      case 'pending': return '待付款';
      case 'overdue': return '逾期';
      case 'refunded': return '已退款';
      default: return '未知';
    }
  };

  const getPaymentMethodText = (method?: string) => {
    switch (method) {
      case 'cash': return '現金';
      case 'card': return '信用卡';
      case 'mobile': return '行動支付';
      case 'online': return '線上支付';
      default: return '-';
    }
  };

  // 過濾支付記錄
  const filteredPayments = payments.filter(payment => {
    const matchesPlate = payment.plateNumber.toLowerCase().includes(searchPlate.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    const matchesMethod = paymentMethodFilter === 'all' || payment.paymentMethod === paymentMethodFilter;
    return matchesPlate && matchesStatus && matchesMethod;
  });

  // 分頁
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  const handleViewPayment = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const handleProcessPayment = async (paymentId: string, method: string) => {
    try {
      const updatedPayments = payments.map(payment => 
        payment.id === paymentId 
          ? { 
              ...payment, 
              status: 'paid' as const,
              paymentMethod: method as any,
              paymentTime: new Date().toLocaleString(),
              transactionId: `TXN${Date.now()}`
            }
          : payment
      );
      
      setPayments(updatedPayments);
      setShowModal(false);
    } catch (error) {
      console.error('處理付款失敗:', error);
    }
  };

  const handleRefund = async (paymentId: string) => {
    if (window.confirm('確定要退款嗎？')) {
      try {
        const updatedPayments = payments.map(payment => 
          payment.id === paymentId 
            ? { ...payment, status: 'refunded' as const }
            : payment
        );
        
        setPayments(updatedPayments);
        setShowModal(false);
      } catch (error) {
        console.error('退款失敗:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  // 統計數據
  const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const paidAmount = payments.filter(p => p.status === 'paid').reduce((sum, payment) => sum + payment.amount, 0);
  const pendingAmount = payments.filter(p => p.status === 'pending').reduce((sum, payment) => sum + payment.amount, 0);
  const overdueAmount = payments.filter(p => p.status === 'overdue').reduce((sum, payment) => sum + payment.amount, 0);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-credit-card me-3"></i>
          支付管理
        </h1>
        <Button variant="primary" onClick={loadPayments}>
          <i className="fas fa-sync-alt me-2"></i>
          重新整理
        </Button>
      </div>

      {/* 統計卡片 */}
      <Row className="mb-4">
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">NT$ {totalAmount}</h3>
              <p className="mb-0">總金額</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">NT$ {paidAmount}</h3>
              <p className="mb-0">已收款</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">NT$ {pendingAmount}</h3>
              <p className="mb-0">待收款</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-danger">NT$ {overdueAmount}</h3>
              <p className="mb-0">逾期款項</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 搜尋和過濾 */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>搜尋車牌號碼</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="請輸入車牌號碼"
                  value={searchPlate}
                  onChange={(e) => setSearchPlate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>狀態篩選</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">全部狀態</option>
                  <option value="pending">待付款</option>
                  <option value="paid">已付款</option>
                  <option value="overdue">逾期</option>
                  <option value="refunded">已退款</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>付款方式</Form.Label>
                <Form.Select
                  value={paymentMethodFilter}
                  onChange={(e) => setPaymentMethodFilter(e.target.value)}
                >
                  <option value="all">全部方式</option>
                  <option value="cash">現金</option>
                  <option value="card">信用卡</option>
                  <option value="mobile">行動支付</option>
                  <option value="online">線上支付</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 支付記錄表格 */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">支付記錄 ({filteredPayments.length} 筆記錄)</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {currentPayments.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-receipt fa-3x text-muted mb-3"></i>
              <p className="text-muted">沒有找到符合條件的支付記錄</p>
            </div>
          ) : (
            <Table responsive striped hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th>車牌號碼</th>
                  <th>停車位</th>
                  <th>停車時長</th>
                  <th>金額</th>
                  <th>狀態</th>
                  <th>付款方式</th>
                  <th>付款時間</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {currentPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="fw-bold">{payment.plateNumber}</td>
                    <td>{payment.spaceNumber}</td>
                    <td>{payment.duration}</td>
                    <td className="fw-bold text-success">NT$ {payment.amount}</td>
                    <td>
                      <Badge bg={getStatusColor(payment.status)}>
                        {getStatusText(payment.status)}
                      </Badge>
                    </td>
                    <td>{getPaymentMethodText(payment.paymentMethod)}</td>
                    <td>{payment.paymentTime || '-'}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewPayment(payment)}
                      >
                        <i className="fas fa-eye"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (
                page === currentPage - 3 ||
                page === currentPage + 3
              ) {
                return <Pagination.Ellipsis key={page} />;
              }
              return null;
            })}
            
            <Pagination.Next 
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* 支付詳情模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>支付詳情</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPayment && (
            <div>
              <Row>
                <Col md={6}>
                  <p><strong>車牌號碼:</strong> {selectedPayment.plateNumber}</p>
                  <p><strong>車主姓名:</strong> {selectedPayment.ownerName}</p>
                  <p><strong>聯絡電話:</strong> {selectedPayment.ownerPhone}</p>
                  <p><strong>停車位:</strong> {selectedPayment.spaceNumber}</p>
                </Col>
                <Col md={6}>
                  <p><strong>進入時間:</strong> {selectedPayment.entryTime}</p>
                  <p><strong>離開時間:</strong> {selectedPayment.exitTime || '尚未離開'}</p>
                  <p><strong>停車時長:</strong> {selectedPayment.duration}</p>
                  <p><strong>應付金額:</strong> <span className="fw-bold text-success">NT$ {selectedPayment.amount}</span></p>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col md={6}>
                  <p><strong>付款狀態:</strong> <Badge bg={getStatusColor(selectedPayment.status)}>{getStatusText(selectedPayment.status)}</Badge></p>
                  <p><strong>付款方式:</strong> {getPaymentMethodText(selectedPayment.paymentMethod)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>付款時間:</strong> {selectedPayment.paymentTime || '-'}</p>
                  <p><strong>交易編號:</strong> {selectedPayment.transactionId || '-'}</p>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            關閉
          </Button>
          {selectedPayment?.status === 'pending' && (
            <>
              <Button 
                variant="success" 
                onClick={() => handleProcessPayment(selectedPayment.id, 'cash')}
                className="me-2"
              >
                現金付款
              </Button>
              <Button 
                variant="primary" 
                onClick={() => handleProcessPayment(selectedPayment.id, 'card')}
              >
                信用卡付款
              </Button>
            </>
          )}
          {selectedPayment?.status === 'paid' && (
            <Button 
              variant="warning" 
              onClick={() => handleRefund(selectedPayment.id)}
            >
              申請退款
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaymentManagement;